import { Head, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { todayStr } from "@/lib/date-utils"
import { equipmentLogsPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface SelectOption {
  id: number
  name: string
}

interface Props {
  equipment_options: SelectOption[]
  vendor_options: SelectOption[]
  preselected_equipment_id: string
  errors?: Record<string, string[]> | null
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Equipment", href: "/equipment" },
  { title: "Maintenance Logs", href: "/equipment_logs" },
  { title: "Add Log", href: "#" },
]

export default function New({
  equipment_options,
  vendor_options,
  preselected_equipment_id,
  errors,
}: Props) {
  const { data, setData, post, processing, transform } = useForm({
    equipment_id: preselected_equipment_id ?? "",
    vendor_id: "",
    log_date: todayStr(),
    notes: "",
    start_odometer: "",
    end_odometer: "",
  })

  transform((data) => ({ equipment_log: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(equipmentLogsPath())
  }

  const inputClass =
    "mt-1 block w-full rounded border border-hpi-light-gray px-3 py-2 focus:border-hpi-active-blue focus:outline-none"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Add Maintenance Log" />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">Add Maintenance Log</h1>

        <form onSubmit={handleSubmit} className="max-w-2xl space-y-4">
          {/* Equipment */}
          <div>
            <label className={labelClass}>Equipment *</label>
            <select
              value={data.equipment_id}
              onChange={(e) => setData("equipment_id", e.target.value)}
              className={inputClass}
            >
              <option value="">-- Select Equipment --</option>
              {equipment_options.map((eq) => (
                <option key={eq.id} value={eq.id}>
                  {eq.name}
                </option>
              ))}
            </select>
            {errors?.equipment_id && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.equipment_id[0]}
              </p>
            )}
          </div>

          {/* Date */}
          <div>
            <label className={labelClass}>Date *</label>
            <input
              type="date"
              value={data.log_date}
              onChange={(e) => setData("log_date", e.target.value)}
              className={inputClass}
            />
            {errors?.log_date && (
              <p className="text-hpi-red mt-1 text-sm">{errors.log_date[0]}</p>
            )}
          </div>

          {/* Vendor */}
          <div>
            <label className={labelClass}>Vendor</label>
            <select
              value={data.vendor_id}
              onChange={(e) => setData("vendor_id", e.target.value)}
              className={inputClass}
            >
              <option value="">-- None --</option>
              {vendor_options.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>

          {/* Odometer */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Start Odometer / Hours</label>
              <input
                type="number"
                step="0.01"
                value={data.start_odometer}
                onChange={(e) => setData("start_odometer", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>End Odometer / Hours</label>
              <input
                type="number"
                step="0.01"
                value={data.end_odometer}
                onChange={(e) => setData("end_odometer", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className={labelClass}>Notes *</label>
            <textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={6}
              className={inputClass}
              placeholder="Describe the maintenance performed..."
            />
            {errors?.notes && (
              <p className="text-hpi-red mt-1 text-sm">{errors.notes[0]}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-4">
            <a
              href={equipmentLogsPath()}
              className="border-hpi-light-gray hover:bg-hpi-pale-blue inline-block rounded border px-4 py-2"
            >
              Cancel
            </a>
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              {processing ? "Saving..." : "Add Log Entry"}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
